import DefaultLayout from "@/layouts/DefaultLayout";
import { NextPage } from "next";
import { NextSeo } from "next-seo";
import useSearchQueryParameter from "@/hooks/useSearchQueryParameter";
import CategoryHeader from "@/components/CategoryHeader";
import CategoryContainer, {
  SearchBtnParam,
} from "@/components/CategoryContainer";
import useLatestProjects from "@/features/project/hooks/useLatestProjects";
import { ArchitectureSearchQueryParameter } from "@/types/ArchitectureSearchQueryParameter";
import ProjectItemList from "@/features/project/components/ProjectItemList";
import ProjectItem from "@/features/project/components/ProjectItem";
import useExistBuildingType from "@/hooks/useExistBuildingTypes";
import publicPath from "@/utils/publicPath";
import CategoryItem from "@/components/CategoryItem";
import SectionContainer from "@/components/SectionContainer";
import CategoryItemContainer from "@/components/CategoryItemContainer";
import SearchLink from "@/components/SearchLink";
import MoreBtnLink from "@/components/MoreBtnLink";
import usePageView from "@/hooks/usePageView";
import useArrayMutation from "@/hooks/useArrayMutation";
import { ProjectResponse } from "@/web-client/api";

const SearchBtnParams: SearchBtnParam<ArchitectureSearchQueryParameter>[] = [
  {
    label: "「ホテル」 × 「リノベーション」",
    query: {
      building_type_id: "5",
      construction_type: "2",
    },
  },
  {
    label: "「マンション」 × 「鉄骨造」",
    query: {
      building_type_id: "2",
      structure_type_id: "3",
    },
  },
  {
    label: "「レストラン」 × 「1,000㎡以下」",
    query: {
      building_type_id: "16",
      max_floor_space: "1000",
    },
  },
  {
    label: "「住宅」×「木造」×「新築」×「100㎡以下」",
    query: {
      building_type_id: "1",
      structure_type_id: "1",
      construction_type: "1",
      max_floor_space: "100",
    },
  },
  {
    label: "SUPPOSE DESIGN OFFICE",
    query: {
      keyword: "SUPPOSE",
    },
  },
];

const description = `TECTUREに登録された建築事例をご紹介します。国内外の建築家・設計士が意匠を凝らしたさまざまなデザイン・スタイルの建築を登録しています。新築・リノベーションなどの工事種別、木造・RC造などの構造など様々な軸から検索することが可能です。`;

const ArchitecturesIndexPage: NextPage = () => {
  usePageView();
  const { queryController } =
    useSearchQueryParameter<ArchitectureSearchQueryParameter>(
      "/architectures/projects/search",
    );
  const existBuildingTypes = useExistBuildingType();
  const { projects: latestProjects, mutate } = useLatestProjects(6);
  const { update } = useArrayMutation<ProjectResponse>();

  return (
    <DefaultLayout hiddenFooter={false}>
      <NextSeo
        title={`建築をさがす`}
        description={description}
        canonical={publicPath("/architectures")}
      />

      <CategoryContainer
        header={
          <CategoryHeader
            title={"建築を探す"}
            description={description}
            backgroundImageUrl="/imgs/bg_architecture.jpg"
          />
        }
      >
        <SectionContainer title="カテゴリから探す" hiddenUnderline={true}>
          <CategoryItemContainer>
            {existBuildingTypes.map((buildingType) => (
              <li key={buildingType.id}>
                <CategoryItem
                  name={buildingType.name}
                  imgUrl={`/imgs/building_type/${buildingType.id}_thumb.jpg`}
                  href={queryController.toPath({
                    building_type_id: buildingType.id.toString(),
                  })}
                />
              </li>
            ))}
          </CategoryItemContainer>
        </SectionContainer>

        <SectionContainer title="おすすめの検索" hiddenUnderline={true}>
          <ul className="Cloud small:px-8">
            {SearchBtnParams.map((param) => (
              <li key={param.label}>
                <SearchLink
                  href={queryController.toPath(param.query)}
                  label={param.label}
                />
              </li>
            ))}
          </ul>
        </SectionContainer>

        <SectionContainer
          title="最近登録されたプロジェクト"
          hiddenUnderline={true}
          moreLink={"/architectures/projects/search"}
        >
          <div className="flex flex-col gap-y-32">
            <div className="px-8">
              <ProjectItemList projects={latestProjects}>
                {latestProjects.map((project) => (
                  <ProjectItem
                    key={project.id}
                    project={project}
                    mutate={(updatedProject) =>
                      mutate(update(latestProjects, updatedProject), false)
                    }
                  />
                ))}
              </ProjectItemList>
            </div>

            <div className="mx-auto">
              <MoreBtnLink href="/architectures/projects/search" />
            </div>
          </div>
        </SectionContainer>
      </CategoryContainer>
    </DefaultLayout>
  );
};

export default ArchitecturesIndexPage;
